import { Text, Toggle, Icon } from '@fluentui/react';
import type { ToolGroupDefinition, ToolSelection } from '../../types/tools';
import styles from './ToolSelector.module.css';

interface ToolGroupCardProps {
  group: ToolGroupDefinition;
  selection: ToolSelection;
  onChange: (toolId: string, enabled: boolean) => void;
  onGroupToggle: (groupId: string, enabled: boolean) => void;
  readOnly?: boolean;
  hint?: string;
}

export default function ToolGroupCard({ group, selection, onChange, onGroupToggle, readOnly, hint }: ToolGroupCardProps) {
  const isAlwaysEnabled = !!group.alwaysEnabled;
  const enabledCount = group.tools.filter((t) => selection[t.id]?.enabled).length;
  const allEnabled = enabledCount === group.tools.length;

  const cardClass = [
    styles.groupCard,
    isAlwaysEnabled ? styles.alwaysEnabled : '',
    isAlwaysEnabled ? styles.fullWidth : '',
  ].filter(Boolean).join(' ');

  return (
    <div className={cardClass}>
      <div className={styles.groupHeader}>
        {group.icon && (
          <Icon iconName={group.icon} className={styles.groupIcon} />
        )}
        <div className={styles.groupTitleBlock}>
          <Text variant="mediumPlus" styles={{ root: { fontWeight: 600, lineHeight: '20px' } }}>
            {group.name}
          </Text>
          {group.description && (
            <Text variant="tiny" styles={{ root: { color: 'var(--ab-text-secondary)' } }}>
              {group.description}
            </Text>
          )}
        </div>
        <div className={styles.headerRight}>
          {isAlwaysEnabled ? (
            <span className={styles.groupBadge}>Always on</span>
          ) : (
            <>
              <span className={styles.countBadge}>{enabledCount} / {group.tools.length}</span>
              {!readOnly && (
                <Toggle
                  checked={allEnabled}
                  onChange={(_, checked) => onGroupToggle(group.id, !!checked)}
                  styles={{ root: { marginBottom: 0 }, pill: { width: 36, height: 18 }, thumb: { width: 12, height: 12 } }}
                />
              )}
            </>
          )}
        </div>
      </div>

      {hint && (
        <Text variant="tiny" styles={{ root: { color: 'var(--ab-warning, #d48c00)', padding: '4px 12px', fontStyle: 'italic' } }}>
          💡 {hint}
        </Text>
      )}

      <div className={styles.toolList}>
        {group.tools.map((tool) => {
          const enabled = selection[tool.id]?.enabled ?? false;
          const isToolAlwaysOn = !!tool.alwaysEnabled;
          const canClick = !readOnly && !isToolAlwaysOn;

          return (
            <div
              key={tool.id}
              className={`${styles.toolRow} ${canClick ? styles.clickable : ''}`}
              onClick={canClick ? () => onChange(tool.id, !enabled) : undefined}
            >
              <div className={styles.toolInfo}>
                <Text variant="small" styles={{ root: { fontWeight: 500 } }}>
                  {tool.name}
                </Text>
                {tool.provider && (
                  <Text variant="tiny" styles={{ root: { color: 'var(--ab-text-secondary)' } }}>
                    {tool.provider}
                  </Text>
                )}
              </div>

              {isToolAlwaysOn ? (
                <Icon iconName="SkypeCheck" className={styles.checkIcon} />
              ) : readOnly ? (
                <div className={`${styles.statusDot} ${enabled ? styles.enabled : styles.disabled}`} />
              ) : (
                <div onClick={(e) => e.stopPropagation()}>
                  <Toggle
                    className={styles.toolToggle}
                    checked={enabled}
                    onChange={(_, checked) => onChange(tool.id, !!checked)}
                    styles={{ root: { marginBottom: 0 } }}
                  />
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
